export function haversineDistanceFeet(lat1, lon1, lat2, lon2) {
  const toRad = (value) => (value * Math.PI) / 180;
  const earthRadiusFeet = 20925524.9;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return earthRadiusFeet * c;
}

export function extractTrackSegments(trackCoordinates) {
  const safeCoordinates = Array.isArray(trackCoordinates) ? trackCoordinates : [];
  const segments = [];

  for (let i = 0; i < safeCoordinates.length; i++) {
    const point = safeCoordinates[i];
    if (point?.command !== 'start') continue;

    const stopPoint = safeCoordinates.slice(i + 1).find((candidate) => candidate?.command === 'stop');
    if (!stopPoint) continue;

    segments.push({
      index: segments.length,
      label: point.label || `Section ${segments.length + 1}`,
      startLat: point.lat,
      startLng: point.lng,
      stopLat: stopPoint.lat,
      stopLng: stopPoint.lng,
    });
  }

  return segments;
}

export function findClosestSampleIndex(samples, targetLat, targetLng, startIndex = 0, { localSearch = false } = {}) {
  let bestIndex = -1;
  let bestSqDist = Number.POSITIVE_INFINITY;
  let movingAway = 0;

  for (let i = startIndex; i < samples.length; i++) {
    const sample = samples[i];
    if (sample?.lat == null || sample?.lng == null) continue;

    const dLat = sample.lat - targetLat;
    const dLng = sample.lng - targetLng;
    const sqDist = dLat * dLat + dLng * dLng;

    if (sqDist < bestSqDist) {
      bestSqDist = sqDist;
      bestIndex = i;
      movingAway = 0;
    } else if (localSearch && bestIndex >= 0) {
      // Stop after moving away consistently — prevents matching to the
      // second pass on closed circuits where the car returns near start/finish
      movingAway++;
      if (movingAway > 20) break;
    }
  }

  const distanceFeet = bestIndex >= 0
    ? haversineDistanceFeet(samples[bestIndex].lat, samples[bestIndex].lng, targetLat, targetLng)
    : Number.POSITIVE_INFINITY;

  return { index: bestIndex, distanceFeet };
}

function getBestLapNumber(laps = []) {
  let bestLap = null;
  for (const lap of laps) {
    if (!lap) continue;
    if (!bestLap || (lap.lapTime || Number.POSITIVE_INFINITY) < (bestLap.lapTime || Number.POSITIVE_INFINITY)) {
      bestLap = lap;
    }
  }
  return bestLap?.lapNum ?? null;
}

function getLapTimeMs(lap) {
  const lapTimeSeconds = Number(lap?.lapTime);
  if (!Number.isFinite(lapTimeSeconds) || lapTimeSeconds <= 0) return null;
  return Math.round(lapTimeSeconds * 1000);
}

export function buildEntryForSegment(lap, segment, thresholdFeet) {
  const samples = lap?.fullData || lap?.data || [];
  if (!samples.length) return null;

  const startMatch = findClosestSampleIndex(samples, segment.startLat, segment.startLng, 0);
  if (startMatch.index < 0 || startMatch.distanceFeet > thresholdFeet) return null;

  const stopMatch = findClosestSampleIndex(samples, segment.stopLat, segment.stopLng, startMatch.index + 1);
  if (stopMatch.index < 0 || stopMatch.distanceFeet > thresholdFeet) return null;

  const entrySample = samples[startMatch.index];
  const exitSample = samples[stopMatch.index];
  const sectionSamples = samples.slice(startMatch.index, stopMatch.index + 1);
  const sectionTimeSeconds = (exitSample.time || 0) - (entrySample.time || 0);

  if (sectionTimeSeconds <= 0) return null;

  return {
    segmentIndex: segment.index,
    segmentLabel: segment.label,
    lap: lap.lapNum,
    driverLabel: lap.driverLabel || null,
    lapKey: lap.driverLabel ? `${lap.driverLabel}:${lap.lapNum}` : `${lap.lapNum}`,
    entryLapMs: Math.round((entrySample.time || 0) * 1000),
    exitLapMs: Math.round((exitSample.time || 0) * 1000),
    sectionTimeMs: Math.round(sectionTimeSeconds * 1000),
    entrySpeedMph: entrySample.speed || 0,
    exitSpeedMph: exitSample.speed || 0,
    minSpeedMph: sectionSamples.reduce((minValue, sample) => {
      const speed = sample?.speed ?? Number.POSITIVE_INFINITY;
      return Math.min(minValue, speed);
    }, Number.POSITIVE_INFINITY),
  };
}

function buildRollingMinimum(validLaps, entriesByLap, segmentCount) {
  if (!validLaps.length || !segmentCount) return null;

  if (validLaps.length === 1) {
    const lap = validLaps[0];
    const entries = entriesByLap.get(lap);
    if (!entries || entries.length !== segmentCount) return null;
    return {
      totalMs: entries.reduce((sum, entry) => sum + entry.sectionTimeMs, 0),
      startLap: lap,
      startSegmentIndex: 0,
      entries,
    };
  }

  let best = null;

  for (let lapIndex = 0; lapIndex < validLaps.length; lapIndex++) {
    const currentLap = validLaps[lapIndex];
    const nextLap = validLaps[lapIndex + 1] ?? null;

    for (let startSegmentIndex = 0; startSegmentIndex < segmentCount; startSegmentIndex++) {
      if (startSegmentIndex > 0 && !nextLap) continue;

      const windowEntries = [];
      let totalMs = 0;
      let valid = true;

      for (let offset = 0; offset < segmentCount; offset++) {
        const segmentIndex = (startSegmentIndex + offset) % segmentCount;
        const sourceLap = startSegmentIndex + offset < segmentCount ? currentLap : nextLap;
        const lapEntries = entriesByLap.get(sourceLap);
        const entry = lapEntries?.find((candidate) => candidate.segmentIndex === segmentIndex);

        if (!entry) {
          valid = false;
          break;
        }

        windowEntries.push(entry);
        totalMs += entry.sectionTimeMs;
      }

      if (valid && (!best || totalMs < best.totalMs)) {
        best = {
          totalMs,
          startLap: currentLap,
          startSegmentIndex,
          entries: windowEntries,
        };
      }
    }
  }

  return best;
}

export function buildSplitTimeReport(sessionData, trackCoordinates, options = {}) {
  const beginLap = options.beginLap ?? 1;
  const thresholdFeet = options.thresholdFeet ?? 200;
  const segments = extractTrackSegments(trackCoordinates);
  const laps = (sessionData?.laps || []).filter((lap) => (lap?.lapNum || 0) >= beginLap);

  if (!segments.length || !laps.length) {
    return null;
  }

  // Use lapKey (driver+lapNum) as unique key when comparing across drivers
  const getLapKey = (lap) => lap.driverLabel ? `${lap.driverLabel}:${lap.lapNum}` : `${lap.lapNum}`;

  const entries = [];
  const entriesByLap = new Map();
  const lapTotals = [];

  // Build lap info for display (lapKey -> { lapNum, driverLabel })
  const lapInfo = new Map();
  for (const lap of laps) {
    const key = getLapKey(lap);
    lapInfo.set(key, { lapNum: lap.lapNum, driverLabel: lap.driverLabel || null });
  }

  for (const lap of laps) {
    const key = getLapKey(lap);
    const lapEntries = [];
    for (const segment of segments) {
      const entry = buildEntryForSegment(lap, segment, thresholdFeet);
      if (entry) {
        lapEntries.push(entry);
        entries.push(entry);
      }
    }

    entriesByLap.set(key, lapEntries);
    lapTotals.push({
      lap: lap.lapNum,
      lapKey: key,
      driverLabel: lap.driverLabel || null,
      totalMs: getLapTimeMs(lap),
      segmentTotalMs: lapEntries.reduce((sum, entry) => sum + entry.sectionTimeMs, 0),
    });
  }

  const validLaps = laps
    .filter((lap) => (entriesByLap.get(getLapKey(lap))?.length || 0) === segments.length)
    .map((lap) => getLapKey(lap));

  const eclectic = segments.map((segment) => {
    const segmentEntries = entries.filter((entry) => entry.segmentIndex === segment.index);
    const bestEntry = segmentEntries.reduce((best, entry) => {
      if (!best || entry.sectionTimeMs < best.sectionTimeMs) return entry;
      return best;
    }, null);

    return {
      segmentIndex: segment.index,
      timeMs: bestEntry?.sectionTimeMs ?? null,
      lap: bestEntry?.lap ?? null,
    };
  });

  const eclecticMap = new Map(
    eclectic
      .filter((item) => item.timeMs != null)
      .map((item) => [item.segmentIndex, item.timeMs])
  );

  const decoratedEntries = entries.map((entry) => {
    const minTime = eclecticMap.get(entry.segmentIndex);
    const consistencyPct = minTime
      ? ((entry.sectionTimeMs - minTime) / minTime) * 100
      : null;

    return {
      ...entry,
      consistencyPct,
      isBestForSegment: minTime != null && entry.sectionTimeMs === minTime,
    };
  });

  const decoratedEntriesByLap = new Map();
  for (const lap of laps) {
    const key = getLapKey(lap);
    decoratedEntriesByLap.set(
      key,
      decoratedEntries.filter((entry) => entry.lapKey === key)
    );
  }

  const rollingMinimum = buildRollingMinimum(validLaps, decoratedEntriesByLap, segments.length);
  const rollingKeySet = new Set(
    (rollingMinimum?.entries || []).map((entry) => `${entry.lapKey}-${entry.segmentIndex}`)
  );

  const finalEntries = decoratedEntries.map((entry) => ({
    ...entry,
    isRollingMinimum: rollingKeySet.has(`${entry.lapKey}-${entry.segmentIndex}`),
  }));

  const lapKeys = laps.map((lap) => getLapKey(lap));
  const validLapObjects = laps.filter((lap) => validLaps.includes(getLapKey(lap)));
  const referenceLap = validLapObjects.reduce((best, lap) => {
    if (!best) return lap;
    return (lap.lapTime || Number.POSITIVE_INFINITY) < (best.lapTime || Number.POSITIVE_INFINITY) ? lap : best;
  }, null);
  const refKey = referenceLap ? getLapKey(referenceLap) : null;
  const referenceLapEntries = refKey ? decoratedEntriesByLap.get(refKey) || [] : [];
  const referenceLapSegmentTotalMs = referenceLapEntries.reduce((sum, entry) => sum + entry.sectionTimeMs, 0);
  const referenceLapTotalMs = getLapTimeMs(referenceLap);

  const eclecticSegmentTotalMs = eclectic.reduce((sum, item) => sum + (item.timeMs || 0), 0);
  const rollingSegmentTotalMs = (rollingMinimum?.entries || []).reduce(
    (sum, entry) => sum + (entry.sectionTimeMs || 0),
    0
  );

  const eclecticTotalMs = referenceLapTotalMs != null
    ? referenceLapTotalMs - referenceLapSegmentTotalMs + eclecticSegmentTotalMs
    : null;
  const rollingMinimumTotalMs = referenceLapTotalMs != null && rollingMinimum
    ? referenceLapTotalMs - referenceLapSegmentTotalMs + rollingSegmentTotalMs
    : null;

  return {
    trackName: sessionData?.track || '',
    sessionId: sessionData?.sessionId || '',
    segments: segments.map(({ label, index }) => ({ label, index })),
    laps: lapKeys,
    lapInfo,
    validLaps,
    entries: finalEntries,
    eclectic,
    eclecticTotal: eclecticTotalMs,
    rollingMinimum: rollingMinimum
      ? {
          ...rollingMinimum,
          segmentTotalMs: rollingSegmentTotalMs,
          totalMs: rollingMinimumTotalMs,
        }
      : null,
    lapTotals,
    bestLapNumber: getBestLapNumber(laps),
    referenceLapNumber: referenceLap?.lapNum ?? null,
    referenceLapTotalMs,
    referenceLapSegmentTotalMs,
    eclecticSegmentTotalMs,
  };
}
